import {
  practiceCardFields,
  type PracticeProjectionSource,
} from "~/lib/matching/practice-projection";
import type { TherapistSearchResult } from "~/server/services/therapist-search";
import type { TherapistCardData } from "./types";

/**
 * Normalises raw `Therapist.languages` entries for display: some rows store
 * a single comma-joined string instead of separate array entries, and many
 * carry trailing spaces or inconsistent casing. Splits on commas, trims,
 * drops empties, capitalises the first letter, and de-duplicates
 * case-insensitively while preserving first-seen order.
 */
export function normalizeLanguages(languages: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const entry of languages) {
    for (const part of entry.split(",")) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
      const key = capitalized.toLowerCase();

      if (seen.has(key)) continue;
      seen.add(key);
      normalized.push(capitalized);
    }
  }

  return normalized;
}

/**
 * Maps a therapist-search payload onto the card's presentational contract.
 * Accepts `TherapistSearchResult` directly; the tRPC-serialized
 * `RouterOutput["therapistRouter"]["search"][number]` is structurally the
 * same shape (superjson preserves `Date` fields across the wire) and is
 * assignable to it.
 */
export function toTherapistCardData(
  therapist: TherapistSearchResult,
): TherapistCardData {
  return {
    id: therapist.id,
    slug: therapist.slug,
    name: therapist.user.name || "Terapeut",
    image: therapist.user.image ?? null,
    tier: therapist.qualificationCategory,
    title: therapist.title,
    isVerified: Boolean(therapist.isVerified),
    isFeatured: therapist.isFeatured,
    offersIntroCall: therapist.offersIntroCall,
    // Already tallied server-side (search-router computes this from the raw
    // review rows and never forwards the rows themselves).
    reviewCount: therapist.reviewCount,
    // Completed-session count, batched server-side in therapist-search.ts.
    sessionsHeld: therapist.sessionsHeld,
    languages: normalizeLanguages(therapist.languages),
    price: therapist.defaultPrice,
    sessionDurationMinutes: therapist.individualSessionDuration,
    couplesTherapyPrice: therapist.couplesTherapyPrice,
    couplesSessionDuration: therapist.couplesSessionDuration,
    videoIntroductionUrl: therapist.videoIntroductionUrl,
    ...practiceCardFields(therapist),
  };
}

export type BreakAvailability = { state: "break"; until: string };

/** Only a break whose end time is still in the future is current. */
export function deriveBreakAvailability(
  therapist: Pick<
    TherapistSearchResult,
    "isCurrentlyOnBreak" | "TherapistBreak"
  >,
): BreakAvailability | null {
  if (!therapist.isCurrentlyOnBreak || !therapist.TherapistBreak?.length) {
    return null;
  }

  const now = new Date();
  const currentBreak = therapist.TherapistBreak.find(
    (breakItem) => new Date(breakItem.endTime) > now,
  );

  if (!currentBreak) return null;

  return {
    state: "break",
    until: new Date(currentBreak.endTime).toISOString(),
  };
}

/**
 * Maps a full therapist row from `getTherapist` or `get3RadnomTherapists`
 * onto the card's presentational contract. This shape includes `user.sessionReviews`
 * but lacks some search-specific fields.
 */
export function toTherapistCardDataFromTherapist(
  therapist: PracticeProjectionSource & {
    id: string;
    slug: string;
    defaultPrice: number;
    individualSessionDuration: number;
    couplesTherapyPrice: number | null;
    couplesSessionDuration: number | null;
    videoIntroductionUrl: string | null;
    qualificationCategory: string | null;
    title: string | null;
    isVerified: boolean | null;
    isFeatured: boolean;
    offersIntroCall: boolean;
    languages: string[];
    reviewCount: number;
    user: {
      name: string | null;
      image: string | null;
      sessionReviews: Array<{ rating?: number }>;
    };
  },
  sessionsHeld: number = 0,
): TherapistCardData {
  return {
    id: therapist.id,
    slug: therapist.slug,
    name: therapist.user.name || "Terapeut",
    image: therapist.user.image ?? null,
    tier: therapist.qualificationCategory,
    title: therapist.title,
    isVerified: Boolean(therapist.isVerified),
    isFeatured: therapist.isFeatured,
    offersIntroCall: therapist.offersIntroCall,
    // therapist.reviewCount is tallied server-side from an unfiltered count;
    // user.sessionReviews here is capped to 3 rows for the rating snippet,
    // so its .length undercounts once a therapist passes 3 reviews.
    reviewCount: therapist.reviewCount,
    sessionsHeld,
    languages: normalizeLanguages(therapist.languages),
    price: therapist.defaultPrice,
    sessionDurationMinutes: therapist.individualSessionDuration,
    couplesTherapyPrice: therapist.couplesTherapyPrice,
    couplesSessionDuration: therapist.couplesSessionDuration,
    videoIntroductionUrl: therapist.videoIntroductionUrl,
    ...practiceCardFields(therapist),
  };
}
